import { v4 as uuid } from 'uuid'
import { getUserId } from '@/services/identity-client'
import { httpPostJson, checkStatus, parseJson } from '@/lib/net'
import { compressImage } from '@/lib/photos'
import { log } from '@/services/logging-service'

interface StoredPhoto {
  uuid: string
  url: string
  public_id: string
}

interface CloudinarySignature {
  cloudinary_key: string
  signature: string
  timestamp: string
}

interface CloudinarySignatureResponse {
  signature: CloudinarySignature
  photoUUID: string
}

interface CloudinaryUploadResponse {
  secure_url: string
  public_id: string
  bytes?: number
  height?: number
  width?: number
  format?: string
}

const storedPhotos: Record<string, StoredPhoto> = {}

const _storePhoto = async (
  image: File | Blob | string
): Promise<StoredPhoto> => {
  performance.mark('time_to_photo_uuid_started')
  const photoUuid = await _uploadPhotoUUID()
  performance.mark('time_to_photo_uuid_finished')

  performance.mark('time_to_photo_vendor_started')
  const { signature } = await requestCloudinarySignature(photoUuid)
  const response = await uploadToCloudinary(signature, image, photoUuid)
  performance.mark('time_to_photo_vendor_finished')

  const photoUUID = performance.measure(
    'time_to_photo_uuid',
    'time_to_photo_uuid_started',
    'time_to_photo_uuid_finished'
  )

  const photoVendor = performance.measure(
    'time_to_photo_vendor',
    'time_to_photo_vendor_started',
    'time_to_photo_vendor_finished'
  )

  const account_id = await getUserId()
  log('upload_photo', {
    account_id,
    uuid: photoUuid,
    bytes: response?.bytes,
    height: response?.height,
    width: response?.width,
    format: response?.format,
    uuid_time: photoUUID?.duration,
    vendor_time: photoVendor?.duration,
  })

  const photo: StoredPhoto = {
    uuid: photoUuid,
    url: response.secure_url,
    public_id: response.public_id,
  }

  if (response.secure_url) {
    storedPhotos[photoUuid] = photo
  }

  // public_id used by the contributor portal
  return photo
}

/**
 * Copy an already uploaded photo
 *
 * @param image - images cloudinary url to copy from
 * @returns url and uuid of the photo
 */
export const duplicatePhoto = (image: string) => _storePhoto(image)

/**
 * Upload a new photo
 * @param image - image file to upload
 * @param compress - whether or not to compress the image before uploading
 * @returns UUID and url for uploaded photo
 */
export const uploadPhoto = async (
  image: File | Blob,
  compress = true
): Promise<StoredPhoto> => {
  const imageToUpload = compress ? await compressImage(image) : image
  return _storePhoto(imageToUpload)
}

/**
 * Request the Cloudinary signature for a photo
 *
 * @param photoUUID - (optional) photo UUID for markup photos. If
 * there's no photoUUID, then it's for a profile photo, which has different
 * signature parameters.
 * @returns the signature to use for the upload
 */
export async function requestCloudinarySignature(
  photoUUID: string
): Promise<CloudinarySignatureResponse>
export async function requestCloudinarySignature(
  photoUUID?: null
): Promise<CloudinarySignature>
export async function requestCloudinarySignature(
  photoUUID: string | null = null
): Promise<CloudinarySignatureResponse | CloudinarySignature> {
  const publicId = await getUserId()
  const uploadPreset = photoUUID ? 'user_content' : 'profiles'

  const body: {
    params: {
      public_id: string
      upload_preset: string
      context?: string
    }
  } = {
    params: {
      public_id: publicId ?? '',
      upload_preset: uploadPreset,
    },
  }

  // Update params for markup photos
  if (photoUUID) {
    body.params.public_id = (publicId ?? '') + '/' + photoUUID
    body.params.context = 'uuid=' + photoUUID
  }

  // Request the cloudinary signature from the API
  const response = await httpPostJson<CloudinarySignature>(
    '/v1/cloudinary/signature',
    body
  )
  return photoUUID ? { signature: response, photoUUID: photoUUID } : response
}

/**
 * Make the upload request to Cloudinary
 *
 * @param apiSignature - returned Cloudinary signature to use
 * @param image - image to upload
 * @param photoUUID - (optional) photo UUID for markup photos. If
 * there's no photoUUID, then it's for a profile photo, which has different
 * signature parameters.
 * @returns successful response or error from Cloudinary
 */
export const uploadToCloudinary = async (
  apiSignature: CloudinarySignature,
  image: File | Blob | string,
  photoUUID: string | null = null
): Promise<CloudinaryUploadResponse> => {
  const key = apiSignature.cloudinary_key
  const signature = apiSignature.signature
  const timestamp = apiSignature.timestamp

  let publicId = (await getUserId()) ?? ''
  const uploadPreset = photoUUID ? 'user_content' : 'profiles'

  // Update public_id for markup photos
  if (photoUUID) {
    publicId += '/' + photoUUID
  }

  // Set the Cloudinary params
  const formData = new FormData()
  formData.append('upload_preset', uploadPreset)
  formData.append('signature', signature)
  formData.append('timestamp', timestamp)
  formData.append('api_key', key)
  formData.append('public_id', publicId)
  formData.append('file', image)

  if (photoUUID) {
    formData.append('context', 'uuid=' + photoUUID)
  }

  const cloudOptions: RequestInit = {
    method: 'POST',
    body: formData,
  }

  // Upload the signed image to Cloudinary
  const response = await fetch(
    window.environment.cloudinaryUrl + '/upload',
    cloudOptions
  )
  await checkStatus(response)
  const parsedResponse = await parseJson<CloudinaryUploadResponse>(response)
  return parsedResponse
}

/**
 * Look up photo by photo ID
 *
 * @param id - UUID of photo to look up
 * @returns URL of photo
 */
export const getPhotoById = (id: string): StoredPhoto | undefined => {
  return storedPhotos[id]
}

/**
 * Post new photo UUID to the API
 *
 * @returns API response from photos endpoint
 */
export const _uploadPhotoUUID = async (): Promise<string> => {
  const time = new Date().toISOString()
  const body = {
    uuid: uuid(),
    created_at: time,
    updated_at: time,
  }

  // Send the photo UUID to the API
  await httpPostJson('/v1/photos', body)
  return body.uuid
}

/**
 * Upload to Cloudinary directly. For photos managed by separate services and provide
 * pre-fetched signatures and presets via graphql
 * @param file - image to upload
 * @param uploadURL - A URL to the cloudinary api with query params
 * @param compress - (optional) to compress the image
 */
export const uploadToCloudinaryDirectly = async (
  file: File | Blob,
  uploadURL: string,
  compress = false
): Promise<CloudinaryUploadResponse> => {
  const searchParams = new URLSearchParams(
    uploadURL.substring(uploadURL.indexOf('?'))
  )
  const image = compress ? await compressImage(file) : file

  const formData = new FormData()
  formData.append('upload_preset', searchParams.get('upload_preset') ?? '')
  formData.append('signature', searchParams.get('signature') ?? '')
  formData.append('timestamp', searchParams.get('timestamp') ?? '')
  formData.append('api_key', searchParams.get('api_key') ?? '')
  formData.append('file', image)

  const cloudOptions: RequestInit = {
    method: 'POST',
    body: formData,
  }

  const response = await fetch(
    window.environment.cloudinaryUrl + '/upload',
    cloudOptions
  )
  const result = (await response.json()) as CloudinaryUploadResponse
  if (!response.ok) {
    throw new Error('Upload failed')
  }

  return result
}
